import {useEffect, useState} from 'react'
import Loader from '../../custom/loader/Loader'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../redux/hooks'
import {GeneralStatistics} from '../../../redux/general/generalSlice'
import {Link} from 'react-router-dom'
import {useAuth} from 'app/modules/auth'
import {ApexOptions} from 'apexcharts'
import ReactApexChart from 'react-apexcharts'

interface StudentCounts {
  total_applications: number
  total_students: number
  total_alumni: number
}

interface FinanceData {
  total_paid?: number
  total_unpaid?: number
  total_invoices?: number
}

interface GenderData {
  male?: number
  female?: number
}

interface AvgAgeData {
  program_level: string
  avg_age: number
}

interface ProgramData {
  program_name: string
  student_count: number
}

interface AcademicYearData {
  academic_year_id: number
  academic_year_name: string
  student_count: number
}

interface AttendanceData {
  status_label: string
  student_count: number
}

interface EvaluationData {
  academic_year_id: number
  academic_year_name: string
  strongly_agree_count: number
  agree_count: number
  strongly_disagree_count: number
  somewhat_agree_count: number
  formatted_created_at?: string
}
const AdminDashboard = () => {
  const [loader, setLoader] = useState(true)
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const {hasPermission} = useAuth()
  const [studentCounts, setStudentCounts] = useState<StudentCounts | null>(null)
  const [finance, setFinance] = useState<FinanceData>({})
  const [lecturerStatistics, setLecturerStatistics] = useState<any>([])
  const [studentGender, setStudentGender] = useState<GenderData>({})
  const [showLecturerCharts, setShowLecturerCharts] = useState(false)
  const [showStudentCharts, setShowStudentCharts] = useState(false)
  const [showEvaluationCharts, setShowEvaluationCharts] = useState(false)
  const [studentAvgAge, setStudentAvgAge] = useState<AvgAgeData[]>([])
  const [studentByProgram, setStudentByProgram] = useState<ProgramData[]>([])
  const [studentByAcademicYear, setStudentByAcademicYear] = useState<AcademicYearData[]>([])
  const [studentAttendance, setStudentAttendance] = useState<AttendanceData[]>([])
  const [evaluationData, setEvaluationData] = useState<EvaluationData[]>([])

  useEffect(() => {
    dispatch(GeneralStatistics()).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setStudentCounts(res.payload.student_counts || null)
        setFinance(res.payload.finance || {})
        setStudentGender(res.payload.studentGender || {})
        setStudentAvgAge(res.payload.studentAvgAge || [])
        setStudentByProgram(res.payload.studentByProgram || [])
        setStudentByAcademicYear(res.payload.studentByAcademicYear || [])
        setStudentAttendance(res.payload.studentAttendance || [])
        setEvaluationData(res.payload.academicYearEvForm || [])
        setLecturerStatistics(res.payload.lecturers || [])
      }
      setLoader(false)
    })
  }, [])

  // Separate the statistics into different categories
  const degreeCategories = lecturerStatistics.degree_categories || []
  const genderCategories = lecturerStatistics.gender_categories || []
  const countryCategories = lecturerStatistics.country_categories || []

  // Calculate totals for percentage calculations
  const degreeTotal = degreeCategories.reduce((sum: number, item: any) => sum + item.count, 0)
  const genderTotal = genderCategories.reduce((sum: number, item: any) => sum + item.count, 0)
  const countryTotal = countryCategories.reduce((sum: number, item: any) => sum + item.count, 0)

  // Degree Pie Chart
  const degreeChartOptions: ApexOptions = {
    chart: {
      type: 'pie',
      height: 350,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
      toolbar: {
        show: true,
      },
    },
    labels: degreeCategories.map((item: any) => item.label),
    colors: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'],
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return `${val.toFixed(1)}%`
      },
    },
    tooltip: {
      y: {
        formatter: function (value: number) {
          const percent = ((value / degreeTotal) * 100).toFixed(1)
          return `${value} (${percent}%)`
        },
      },
    },
    legend: {
      position: 'bottom',
    },
    title: {
      text: 'Lecturers by Degree',
      align: 'center',
    },
  }

  // Convert counts to percentages for pie charts
  const degreeChartSeries = degreeCategories.map((item: any) => item.count)

  // Gender Pie Chart
  const genderChartOptions: ApexOptions = {
    chart: {
      type: 'pie',
      height: 350,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
      toolbar: {
        show: true,
      },
    },
    labels: genderCategories.map((item: any) => item.label),
    colors: ['#36A2EB', '#FF6384', '#4BC0C0'], // Blue for Male, Pink for Female, Teal for Other
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return `${val.toFixed(1)}%`
      },
    },
    tooltip: {
      y: {
        formatter: function (value: number) {
          const percent = ((value / genderTotal) * 100).toFixed(1)
          return `${value} (${percent}%)`
        },
      },
    },
    legend: {
      position: 'bottom',
    },
    title: {
      text: 'Lecturers by Gender',
      align: 'center',
    },
  }

  const genderChartSeries = genderCategories.map((item: any) => item.count)

  // Country Bar Chart with percentages
  const countryChartSeries = [
    {
      name: 'Percentage',
      data: countryCategories.map((item: any) => ((item.count / countryTotal) * 100).toFixed(1)),
    },
  ]

  const countryChartLabels = countryCategories.map((item: any) => item.label)

  // Generate distinct colors for each country bar
  const countryChartColors = countryCategories.map((item: any, index: number) => {
    const colors = [
      '#FF6384',
      '#36A2EB',
      '#FFCE56',
      '#4BC0C0',
      '#9966FF',
      '#FF9F40',
      '#8AC249',
      '#EA5545',
      '#F46A9B',
      '#EF9B20',
      '#EDBF33',
      '#87BC45',
      '#27AEEF',
      '#B33DC6',
    ]
    return colors[index % colors.length]
  })

  const countryChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
      toolbar: {
        show: true,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '70%',
        distributed: true,
      },
    },
    colors: countryChartColors,
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return `${val}%`
      },
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        colors: ['#333'],
      },
      offsetY: -20,
    },
    xaxis: {
      categories: countryChartLabels,
      labels: {
        style: {
          fontSize: '12px',
        },
        rotate: -45,
      },
    },
    yaxis: {
      title: {
        text: '',
      },
      max: 100,
      labels: {
        formatter: function (val: number) {
          return `${val}%`
        },
      },
    },
    tooltip: {
      y: {
        formatter: function (value: number) {
          return `${value}%`
        },
      },
    },
    title: {
      text: 'Lecturers by Country',
      align: 'center',
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: '100%',
          },
          dataLabels: {
            enabled: false,
          },
          plotOptions: {
            bar: {
              columnWidth: '50%',
            },
          },
          xaxis: {
            labels: {
              rotate: -90,
            },
          },
        },
      },
    ],
  }

  // Prepare student gender data for chart
  const studentGenderSeries = [studentGender.male || 0, studentGender.female || 0]

  const studentGenderLabels = ['Male', 'Female']
  const studentGenderColors = ['#6EB5FF', '#FF9BBD']
  const totalStudentsByGender = studentGenderSeries.reduce((sum, value) => sum + value, 0)

  const studentGenderOptions: ApexOptions = {
    chart: {
      type: 'pie',
      height: 350,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
    },
    labels: studentGenderLabels,
    colors: studentGenderColors,
    legend: {
      position: 'bottom',
      labels: {
        colors: '#333',
        useSeriesColors: false,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(1)}%`,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
      },
    },
    tooltip: {
      y: {
        formatter: (value: number) => {
          const percent = totalStudentsByGender
            ? ((value / totalStudentsByGender) * 100).toFixed(1)
            : '0.0'
          return `${value} (${percent}%)`
        },
      },
    },
    plotOptions: {
      pie: {
        donut: {},
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {width: '100%'},
          legend: {position: 'bottom'},
        },
      },
    ],
  }

  const avgAgeSeries = studentAvgAge.map((item) => item.avg_age)
  const avgAgeLabels = studentAvgAge.map((item) => item.program_level)
  const maxAge = Math.max(...avgAgeSeries, 30)

  const avgAgeOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 5,
        distributed: true, // <-- This is the key property you're missing
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val.toString() // Show the numeric value
      },
      offsetY: 0, // Center vertically on the bar
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        colors: ['#ffffff'], // White text for better contrast
      },
      dropShadow: {
        enabled: true,
        top: 1,
        left: 1,
        blur: 1,
        color: '#000000',
        opacity: 0.45,
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: avgAgeLabels,
      labels: {
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
      },
    },
    yaxis: {
      min: 0,
      max: maxAge + 5,
    },
    fill: {
      opacity: 1,
    },
    colors: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'], // Colors for each bar
    tooltip: {
      y: {
        formatter: function (val: number) {
          return val + ' years'
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: '100%',
          },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
  }

  const totalStudents = studentByProgram.reduce((sum, program) => sum + program.student_count, 0)
  const programSeries = [
    {
      name: 'Students',
      data: studentByProgram.map((item) => {
        return totalStudents > 0 ? Math.round((item.student_count / totalStudents) * 100) : 0
      }),
    },
  ]

  const programLabels = studentByProgram.map((item) => item.program_name)

  // Chart options for program distribution
  const programOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        distributed: true,
        borderRadius: 5,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return `${val}%` // Add percentage sign
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        colors: ['#333'],
      },
    },
    colors: ['#4CAF50', '#2196F3', '#FFC107', '#9C27B0', '#607D8B'],
    xaxis: {
      categories: programLabels,
      labels: {
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
        rotate: -45,
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: function (val: number) {
          return `${val}%`
        },
      },
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return `${val}% (${Math.round((val / 100) * totalStudents)} students)`
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: '100%',
          },
          dataLabels: {
            enabled: false,
          },
          xaxis: {
            labels: {
              rotate: -90,
            },
          },
        },
      },
    ],
  }

  const totalStudentsByYear = studentByAcademicYear.reduce(
    (sum, year) => sum + year.student_count,
    0
  )

  // Prepare data for the academic year chart
  const academicYearSeries = [
    {
      name: 'Students',
      data: studentByAcademicYear.map((item) => {
        return totalStudentsByYear > 0
          ? parseFloat(((item.student_count / totalStudentsByYear) * 100).toFixed(1))
          : 0
      }),
    },
  ]

  const academicYearLabels = studentByAcademicYear.map((item) => item.academic_year_name)

  // Chart options for academic year distribution
  const academicYearOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
    },
    colors: [
      '#FF4560',
      '#775DD0',
      '#00E396',
      '#FEB019',
      '#008FFB',
      '#F46036',
      '#2E294E',
      '#1B998B',
      '#A44A3F',
      '#6C464F',
    ],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 5,
        distributed: true,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return `${val}%`
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        colors: ['#333'],
      },
    },
    xaxis: {
      categories: academicYearLabels,
      labels: {
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
        rotate: -45,
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: function (val: number) {
          return `${val}%`
        },
      },
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          const count = Math.round((val / 100) * totalStudentsByYear)
          return `${val}% (${count} ${count === 1 ? 'student' : 'students'})`
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: '100%',
          },
          dataLabels: {
            enabled: false,
          },
          xaxis: {
            labels: {
              rotate: -90,
            },
          },
        },
      },
    ],
  }

  const totalAttendance = studentAttendance.reduce((sum, item) => sum + item.student_count, 0)

  // Prepare chart data
  const attendanceSeries = studentAttendance.map((item) => {
    return totalAttendance > 0
      ? parseFloat(((item.student_count / totalAttendance) * 100).toFixed(1))
      : 0
  })

  const attendanceLabels = studentAttendance.map((item) => item.status_label)

  // Color mapping for each status
  const statusColors: Record<string, string> = {
    Present: '#4CAF50', // Green
    Absent: '#F44336', // Red
    'Absent Justified': '#FF9800', // Orange
    Late: '#2196F3', // Blue
  }

  // Chart options
  const attendanceOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
    },
    colors: attendanceLabels.map((label) => statusColors[label] || '#607D8B'), // Fallback color
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 5,
        distributed: true,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return `${val}%`
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        colors: ['#fff'],
      },
    },
    xaxis: {
      categories: attendanceLabels,
      labels: {
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: function (val: number) {
          return `${val}%`
        },
      },
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          const count = Math.round((val / 100) * totalAttendance)
          return `${val}% (${count} ${count === 1 ? 'record' : 'records'})`
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: '100%',
          },
          dataLabels: {
            enabled: false,
          },
        },
      },
    ],
  }

  const parseBackendData = (data: any[]): EvaluationData[] => {
    return data.map((item) => ({
      ...item,
      strongly_agree_count: Number(item.strongly_agree_count) || 0,
      agree_count: Number(item.agree_count) || 0,
      strongly_disagree_count: Number(item.strongly_disagree_count) || 0,
      somewhat_agree_count: Number(item.somewhat_agree_count) || 0,
    }))
  }

  const processEvaluationData = (data: EvaluationData[]) => {
    return data.map((year) => {
      const total =
        year.strongly_agree_count +
        year.agree_count +
        year.strongly_disagree_count +
        year.somewhat_agree_count

      return {
        ...year,
        strongly_agree_percent: total > 0 ? (year.strongly_agree_count / total) * 100 : 0,
        agree_percent: total > 0 ? (year.agree_count / total) * 100 : 0,
        strongly_disagree_percent: total > 0 ? (year.strongly_disagree_count / total) * 100 : 0,
        somewhat_agree_percent: total > 0 ? (year.somewhat_agree_count / total) * 100 : 0,
        total_responses: total,
      }
    })
  }

  const processedData = processEvaluationData(parseBackendData(evaluationData))

  const evaluationSeries = [
    {
      name: 'Tout à fait d’accord / Strongly Agree',
      data: processedData.map((y) => y.strongly_agree_percent),
    },
    {
      name: 'D’accord/ Agree',
      data: processedData.map((y) => y.agree_percent),
    },
    {
      name: 'Peu d’accord / Somewhat Disagree',
      data: processedData.map((y) => y.somewhat_agree_percent),
    },
    {
      name: 'Pas du tout d’accord / Strongly Disagree',
      data: processedData.map((y) => y.strongly_disagree_percent),
    },
  ]

  // Chart options
  const evaluationOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 400,
      stacked: false,
      toolbar: {
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
    },
    colors: ['#4CAF50', '#8BC34A', '#FFC107', '#F44336'],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 6,
        dataLabels: {
          position: 'top',
          hideOverflowingLabels: false,
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => (val > 0 ? `${val.toFixed(val < 5 ? 1 : 0)}%` : ''),
      style: {
        fontSize: '11px',
        fontWeight: 'bold',
        colors: ['#fff'],
      },
    },
    xaxis: {
      categories: processedData.map((y) => y.academic_year_name),
      labels: {
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: (val: number) => `${val}%`,
      },
    },
    tooltip: {
      shared: false,
      y: {
        formatter: (val: number, {seriesIndex, dataPointIndex}) => {
          const year = processedData[dataPointIndex]
          const counts = [
            year.strongly_agree_count,
            year.agree_count,
            year.somewhat_agree_count,
            year.strongly_disagree_count,
          ]
          return `${val.toFixed(1)}% (${counts[seriesIndex]} of ${year.total_responses})`
        },
      },
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      markers: {
        radius: 12,
        width: 12,
        height: 12,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 450,
          },
          plotOptions: {
            bar: {
              columnWidth: '45%',
            },
          },
          dataLabels: {
            style: {
              fontSize: '9px',
            },
          },
        },
      },
    ],
  }

  if (loader) return <Loader />

  return (
    <div className='container-fluid px-4'>
      {/* Student Statistics Cards */}
      {studentCounts && (
        <div className='row g-4'>
          {/* Applications Card */}
          <div className='col-md-4'>
            <Link
              to={
                hasPermission('student-applications-list')
                  ? '/student/student-applications-list'
                  : '#'
              }
              className='text-decoration-none'
            >
              <div className='card bg-primary text-white bg-gradient shadow h-100 rounded-4'>
                <div className='card-body d-flex align-items-center'>
                  <i className='fas fa-file-alt mx-4' style={{fontSize: '3rem'}}></i>
                  <div>
                    <div className='fs-1 fw-bold'>{studentCounts.total_applications}</div>
                    <div className='fs-3'>{t('DASHBOARD.TOTAL_APPLICATIONS')}</div>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Students Card */}
          <div className='col-md-4'>
            <Link
              to={hasPermission('student-applications-list') ? '/student/students-list' : '#'}
              className='text-decoration-none'
            >
              <div className='card bg-success text-white bg-gradient shadow h-100 rounded-4'>
                <div className='card-body d-flex align-items-center'>
                  <i className='fas fa-user-graduate mx-4' style={{fontSize: '3rem'}}></i>
                  <div>
                    <div className='fs-1 fw-bold'>{studentCounts.total_students}</div>
                    <div className='fs-3'>{t('DASHBOARD.TOTAL_STUDENTS')}</div>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Alumni Card */}
          <div className='col-md-4'>
            <Link
              to={
                hasPermission('student-applications-list') ? '/student/alumni-students-list' : '#'
              }
              className='text-decoration-none'
            >
              <div className='card bg-info text-white bg-gradient shadow h-100 rounded-4'>
                <div className='card-body d-flex align-items-center'>
                  <i className='fas fa-user-tie mx-4' style={{fontSize: '3rem'}}></i>
                  <div>
                    <div className='fs-1 fw-bold'>{studentCounts.total_alumni}</div>
                    <div className='fs-3'>{t('DASHBOARD.TOTAL_ALUMNI')}</div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* Finance Statistics */}
      <div className='row g-2 mt-4'>
        <div className='col-md-4'>
          <div className='card text-white bg-success p-2 h-100'>
            <h6 className='card-title mb-1'>{t('LABEL.TOTAL_PAID')}</h6>
            <span className='badge bg-light text-dark fs-6'>{finance?.total_paid ?? 0}</span>
          </div>
        </div>
        <div className='col-md-4'>
          <div className='card text-white bg-danger p-2 h-100'>
            <h6 className='card-title mb-1'>{t('LABEL.TOTAL_DUE')}</h6>
            <span className='badge bg-light text-dark fs-6'>{finance?.total_unpaid ?? 0}</span>
          </div>
        </div>
        <div className='col-md-4'>
          <div className='card text-white bg-warning p-2 h-100'>
            <h6 className='card-title mb-1'>{t('LABEL.TOTAL_AMOUNT')}</h6>
            <span className='badge bg-light text-dark fs-6'>{finance?.total_invoices ?? 0}</span>
          </div>
        </div>
      </div>

      <div className='card shadow-sm h-100 mt-5'>
        <div
          className='card-header bg-primary'
          style={{minHeight: '30px'}}
          onClick={() => setShowLecturerCharts(!showLecturerCharts)}
        >
          <h3 className='text-white mt-3'>{t('MENU.LECTURERS')}</h3>
          <button type='button' className='btn btn-link btn-sm text-white'>
            <span className={`fa fa-caret-${showLecturerCharts ? 'down' : 'up'}`}></span>
          </button>
        </div>
        {showLecturerCharts && (
          <div className='card-body'>
            <div className='row'>
              {degreeCategories.length > 0 && (
                <div className='col-md-6 mb-5'>
                  <div className='card shadow-sm'>
                    <div className='card-body'>
                      <ReactApexChart
                        options={degreeChartOptions}
                        series={degreeChartSeries}
                        type='pie'
                        height={350}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Gender Pie Chart */}
              {genderCategories.length > 0 && (
                <div className='col-md-6 mb-5'>
                  <div className='card shadow-sm'>
                    <div className='card-body'>
                      <ReactApexChart
                        options={genderChartOptions}
                        series={genderChartSeries}
                        type='pie'
                        height={350}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Country Bar Chart - Full width */}
              {countryCategories.length > 0 && (
                <div className='col-12 mb-5'>
                  <div className='card shadow-sm'>
                    <div className='card-body'>
                      <ReactApexChart
                        options={countryChartOptions}
                        series={countryChartSeries}
                        type='bar'
                        height={350}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className='card shadow-sm h-100 mt-5'>
        <div
          className='card-header bg-primary'
          style={{minHeight: '30px'}}
          onClick={() => setShowStudentCharts(!showStudentCharts)}
        >
          <h3 className='text-white mt-3'>{t('MENU.STUDENTS')}</h3>
          <button type='button' className='btn btn-link btn-sm text-white'>
            <span className={`fa fa-caret-${showStudentCharts ? 'down' : 'up'}`}></span>
          </button>
        </div>
        {showStudentCharts && (
          <div className='row'>
            <div className='col-sm-12 col-md-6'>
              <div className='card-body'>
                <h5 className='text-center mb-3'>Gender</h5>
                <ReactApexChart
                  options={studentGenderOptions}
                  series={studentGenderSeries}
                  type='pie'
                  height={350}
                  width={'100%'}
                />
              </div>
            </div>
            <div className='col-sm-12 col-md-6'>
              <div className='card-body'>
                <h5 className='text-center mb-3'>Average Age</h5>
                {studentAvgAge.length > 0 ? (
                  <ReactApexChart
                    options={avgAgeOptions}
                    series={[{name: 'Average Age', data: avgAgeSeries}]}
                    type='bar'
                    height={350}
                    width={'100%'}
                  />
                ) : (
                  <div className='text-center py-5'>No age data available</div>
                )}
              </div>
            </div>
            <div className='col-sm-12 col-md-6'>
              <div className='card-body'>
                <h5 className='text-center mb-3'>Student by Program</h5>
                {studentByProgram.length > 0 ? (
                  <ReactApexChart
                    options={programOptions}
                    series={programSeries}
                    type='bar'
                    height={350}
                    width={'100%'}
                  />
                ) : (
                  <div className='text-center py-5'>No program data available</div>
                )}
              </div>
            </div>
            <div className='col-sm-12 col-md-6'>
              <div className='card-body'>
                <h5 className='text-center mb-3'>Student by Academic Year</h5>
                {studentByAcademicYear.length > 0 ? (
                  <ReactApexChart
                    options={academicYearOptions}
                    series={academicYearSeries}
                    type='bar'
                    height={350}
                    width={'100%'}
                  />
                ) : (
                  <div className='text-center py-5'>No academic year data available</div>
                )}
              </div>
            </div>
            <div className='col-sm-12 col-md-6'>
              <div className='card-body'>
                <h5 className='text-center mb-3'>Student Attendance</h5>
                {studentAttendance.length > 0 ? (
                  <>
                    <ReactApexChart
                      options={attendanceOptions}
                      series={[{name: 'Attendance', data: attendanceSeries}]}
                      type='bar'
                      height={350}
                      width={'100%'}
                    />
                  </>
                ) : (
                  <div className='text-center py-5'>No attendance data available</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className='card shadow-sm h-100 mt-5'>
        <div
          className='card-header bg-primary'
          style={{minHeight: '30px'}}
          onClick={() => setShowEvaluationCharts(!showEvaluationCharts)}
        >
          <h3 className='text-white mt-3'>{t('LABEL.FEEDBACK_AND_EVALUATION')}</h3>
          <button type='button' className='btn btn-link btn-sm text-white'>
            <span className={`fa fa-caret-${showEvaluationCharts ? 'down' : 'up'}`}></span>
          </button>
        </div>
        {showEvaluationCharts && (
          <div className='card-body'>
            {evaluationData.length > 0 ? (
              <>
                <ReactApexChart
                  options={evaluationOptions}
                  series={evaluationSeries}
                  type='bar'
                  height={350}
                  width={'100%'}
                />
              </>
            ) : (
              <div className='text-center py-5'>No evaluation data available</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminDashboard
